import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeftRight, Calendar, Search, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(value);
};

const TransactionsPage = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    lines: [
      { account_code: '', debit: 0, credit: 0, description: '' },
      { account_code: '', debit: 0, credit: 0, description: '' }
    ]
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transRes, accRes] = await Promise.all([
        axios.get(`${API}/transactions`),
        axios.get(`${API}/accounts`)
      ]);
      setTransactions(transRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      lines: [
        { account_code: '', debit: 0, credit: 0, description: '' },
        { account_code: '', debit: 0, credit: 0, description: '' }
      ]
    });
    setDialogOpen(true);
  };

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { account_code: '', debit: 0, credit: 0, description: '' }]
    });
  };

  const removeLine = (index) => {
    if (formData.lines.length <= 2) return;
    setFormData({
      ...formData,
      lines: formData.lines.filter((_, i) => i !== index)
    });
  };

  const updateLine = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setFormData({ ...formData, lines: newLines });
  };

  const totalDebit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.debit) || 0), 0);
  const totalCredit = formData.lines.reduce((sum, l) => sum + (parseFloat(l.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async () => {
    if (!formData.date || !formData.description) {
      toast.error('Data e descrizione sono obbligatori');
      return;
    }
    
    if (!isBalanced) {
      toast.error('Dare e Avere devono essere uguali');
      return;
    }

    const validLines = formData.lines.filter(l => l.account_code && (l.debit > 0 || l.credit > 0));
    if (validLines.length < 2) {
      toast.error('Servono almeno 2 righe valide');
      return;
    }

    try {
      await axios.post(`${API}/transactions`, {
        ...formData,
        lines: validLines.map(l => ({
          ...l,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0
        }))
      });
      toast.success('Movimento registrato');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (transaction) => {
    if (!window.confirm(`Eliminare il movimento "${transaction.description}"?`)) return;

    try {
      await axios.delete(`${API}/transactions/${transaction.id}`);
      toast.success('Movimento eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const filteredTransactions = transactions.filter(t =>
    t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.date.includes(searchQuery)
  );

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="transactions-loading">
        <Skeleton className="h-10 w-64" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Movimenti</h1>
          <p className="text-muted-foreground mt-1">{transactions.length} registrazioni</p>
        </div>
        {canEdit && (
          <Button 
            onClick={handleOpenDialog}
            className="bg-primary text-primary-foreground hover:bg-primary/90 btn-primary-glow"
            data-testid="add-transaction-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuova Registrazione
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input 
              placeholder="Cerca per descrizione o data..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background"
              data-testid="search-transactions-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Transactions List */}
      {filteredTransactions.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <ArrowLeftRight size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">Nessun movimento trovato</p>
            {canEdit && (
              <Button 
                variant="link" 
                className="text-primary mt-2"
                onClick={handleOpenDialog}
              >
                Registra il primo movimento
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredTransactions.map(transaction => (
            <Card 
              key={transaction.id} 
              className="bg-card border-border card-hover"
              data-testid={`transaction-${transaction.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge variant="outline" className="text-xs">
                        <Calendar size={12} className="mr-1" />
                        {transaction.date}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        di {transaction.created_by}
                      </span>
                    </div>
                    <h3 className="font-medium text-foreground mb-3">{transaction.description}</h3>
                    
                    {/* Transaction lines */}
                    <div className="space-y-1 text-sm">
                      {transaction.lines.map((line, i) => {
                        const account = accounts.find(a => a.code === line.account_code);
                        return (
                          <div key={i} className="flex items-center gap-4 text-muted-foreground">
                            <span className="font-mono text-xs text-primary w-12">{line.account_code}</span>
                            <span className="flex-1 truncate">{account?.name || line.account_code}</span>
                            <span className="font-mono w-24 text-right">
                              {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                            </span>
                            <span className="font-mono w-24 text-right">
                              {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {user?.role === 'admin' && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDelete(transaction)}
                      className="text-muted-foreground hover:text-destructive"
                      data-testid={`delete-transaction-${transaction.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Transaction Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nuova Registrazione</DialogTitle>
            <DialogDescription>
              Inserisci i dati del movimento in partita doppia
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Data</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="bg-background"
                  data-testid="transaction-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Descrizione</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="es. Pagamento fornitore"
                  className="bg-background"
                  data-testid="transaction-description-input"
                />
              </div>
            </div>

            {/* Lines header */}
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider px-2">
              <div className="col-span-5">Conto</div>
              <div className="col-span-3 text-right">Dare</div>
              <div className="col-span-3 text-right">Avere</div>
              <div className="col-span-1"></div>
            </div>

            {/* Lines */}
            {formData.lines.map((line, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-center" data-testid={`transaction-line-${index}`}>
                <div className="col-span-5">
                  <Select 
                    value={line.account_code || "select"} 
                    onValueChange={(v) => updateLine(index, 'account_code', v === "select" ? "" : v)}
                  >
                    <SelectTrigger className="bg-background" data-testid={`line-account-${index}`}>
                      <SelectValue placeholder="Seleziona conto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="select" disabled>Seleziona conto</SelectItem>
                      {accounts.map(acc => (
                        <SelectItem key={acc.code} value={acc.code}>
                          {acc.code} - {acc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.debit || ''}
                    onChange={(e) => updateLine(index, 'debit', e.target.value)}
                    placeholder="0.00"
                    className="bg-background text-right font-mono"
                    data-testid={`line-debit-${index}`}
                  />
                </div>
                <div className="col-span-3">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={line.credit || ''}
                    onChange={(e) => updateLine(index, 'credit', e.target.value)}
                    placeholder="0.00"
                    className="bg-background text-right font-mono"
                    data-testid={`line-credit-${index}`}
                  />
                </div>
                <div className="col-span-1 flex justify-center">
                  {formData.lines.length > 2 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLine(index)}
                      className="text-muted-foreground hover:text-destructive h-8 w-8 p-0"
                    >
                      <X size={14} />
                    </Button>
                  )}
                </div>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={addLine}
              className="w-full"
              data-testid="add-line-btn"
            >
              <Plus size={14} className="mr-2" />
              Aggiungi riga
            </Button>

            {/* Totals */}
            <div className="border-t border-border pt-4">
              <div className="grid grid-cols-12 gap-2 text-sm font-medium">
                <div className="col-span-5 text-foreground">Totali</div>
                <div className="col-span-3 text-right font-mono text-foreground">
                  {formatCurrency(totalDebit)}
                </div>
                <div className="col-span-3 text-right font-mono text-foreground">
                  {formatCurrency(totalCredit)}
                </div>
                <div className="col-span-1"></div>
              </div>
              {!isBalanced && totalDebit + totalCredit > 0 && (
                <p className="text-destructive text-xs mt-2">
                  Differenza: {formatCurrency(Math.abs(totalDebit - totalCredit))}
                </p>
              )}
              {isBalanced && totalDebit > 0 && (
                <p className="text-green-400 text-xs mt-2">Bilanciato</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!isBalanced || totalDebit === 0}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="save-transaction-btn"
            >
              Registra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsPage;
